import React from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Container } from '../components/ui/Container';
import { Section } from '../components/ui/Section';
import { Shield, Globe, MapPin, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Compliance() {
  return <Layout data-id="element-1591">
      <Section spacing="lg" data-id="element-1592">
        <Container size="lg" data-id="element-1593">
          {/* Hero */}
          <div className="mb-12 text-center animate-fade-in-up" data-id="element-1594">
            <Badge variant="primary" size="lg" className="mb-6" data-id="element-1595">
              Regulatory Compliance
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-100 mb-6" data-id="element-1596">
              Compliance Guides
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto" data-id="element-1597">
              Navigate global data protection laws and understand your
              obligations. Select a compliance framework to view specific
              requirements.
            </p>
          </div>

          {/* Compliance Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1598">
            {/* Global Standards */}
            <ComplianceCard icon={<Globe className="h-8 w-8" data-id="element-1600" />} badge="Global" title="Global Standards" description="International cybersecurity frameworks and best practices including globally recognized security standards and data protection regulations." link="/compliance/global" features={['Cybersecurity frameworks', 'Information security standards', 'Security controls catalog', 'Data protection regulations', 'International best practices']} color="cyan" delay="100" data-id="element-1599" />

            {/* Indonesia */}
            <ComplianceCard icon={<MapPin className="h-8 w-8" data-id="element-1602" />} badge="Indonesia" title="Indonesia (UU PDP)" description="Comprehensive guide to Indonesia's Personal Data Protection Law (UU PDP), implementation requirements, and national cybersecurity regulations." link="/compliance/indonesia" features={['UU PDP No. 27 Tahun 2022', 'Data protection requirements', 'Cross-border data transfer', 'Breach notification rules', 'National cybersecurity framework']} color="emerald" delay="200" data-id="element-1601" />
          </div>

          {/* Additional Info */}
          <div className="mt-12 max-w-4xl mx-auto animate-fade-in-up animation-delay-300" data-id="element-1603">
            <Card glass padding="lg" className="border-cyan-500/20" data-id="element-1604">
              <div className="flex items-start space-x-4" data-id="element-1605">
                <div className="h-12 w-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 flex-shrink-0" data-id="element-1606">
                  <Shield className="h-6 w-6" data-id="element-1607" />
                </div>
                <div data-id="element-1608">
                  <h3 className="text-lg font-bold text-slate-100 mb-2" data-id="element-1609">
                    Standards-Aligned Approach
                  </h3>
                  <p className="text-sm text-slate-400 mb-4" data-id="element-1610">
                    Our compliance guides are aligned with international
                    standards and frameworks. Additional sectoral, risk,
                    privacy, or cloud-specific standards are referenced where
                    contextually relevant.
                  </p>
                  <Link to="/about/standards" className="inline-flex items-center text-cyan-400 hover:text-cyan-300 font-semibold transition-colors text-sm" data-id="element-1611">
                    View Standards Mapping
                    <ArrowRight className="ml-2 h-4 w-4" data-id="element-1612" />
                  </Link>
                </div>
              </div>
            </Card>
          </div>
        </Container>
      </Section>
    </Layout>;
}
interface ComplianceCardProps {
  icon: React.ReactNode;
  badge: string;
  title: string;
  description: string;
  link: string;
  features: string[];
  color: 'cyan' | 'emerald';
  delay: string;
}
function ComplianceCard({
  icon,
  badge,
  title,
  description,
  link,
  features,
  color,
  delay
}: ComplianceCardProps) {
  const colors = {
    cyan: {
      badge: 'primary',
      icon: 'text-cyan-400 bg-cyan-500/10',
      iconHover: 'group-hover:bg-cyan-500/20',
      border: 'group-hover:border-cyan-500/30',
      glow: 'group-hover:shadow-[0_0_30px_rgba(6,182,212,0.3)]'
    },
    emerald: {
      badge: 'success',
      icon: 'text-emerald-400 bg-emerald-500/10',
      iconHover: 'group-hover:bg-emerald-500/20',
      border: 'group-hover:border-emerald-500/30',
      glow: 'group-hover:shadow-[0_0_30px_rgba(16,185,129,0.3)]'
    }
  };
  const colorConfig = colors[color];
  return <Link to={link} data-id="element-1613">
      <Card glass padding="lg" className={`group relative overflow-hidden ${colorConfig.glow} transition-all duration-300 animate-fade-in-up animation-delay-${delay} h-full`} data-id="element-1614">
        {/* Cyber Border */}
        <div className={`absolute inset-0 rounded-2xl border border-slate-800 ${colorConfig.border} transition-colors`} data-id="element-1615"></div>

        {/* Content */}
        <div className="relative z-10" data-id="element-1616">
          {/* Icon & Badge */}
          <div className="flex items-start justify-between mb-6" data-id="element-1617">
            <div className={`h-14 w-14 rounded-xl ${colorConfig.icon} ${colorConfig.iconHover} flex items-center justify-center transition-all duration-300`} data-id="element-1618">
              {icon}
            </div>
            <Badge variant={colorConfig.badge as any} size="sm" data-id="element-1619">
              {badge}
            </Badge>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-slate-100 mb-3 font-mono group-hover:text-cyan-400 transition-colors" data-id="element-1620">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-slate-400 mb-6 leading-relaxed" data-id="element-1621">
            {description}
          </p>

          {/* Features */}
          <div className="mb-6" data-id="element-1622">
            <h4 className="text-xs font-semibold text-slate-500 uppercase mb-3" data-id="element-1623">
              Includes:
            </h4>
            <ul className="space-y-2" data-id="element-1624">
              {features.map((feature, index) => <li key={index} className="flex items-start text-sm text-slate-300" data-id="element-1625">
                  <CheckCircle className="h-4 w-4 text-cyan-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-1626" />
                  <span data-id="element-1627">{feature}</span>
                </li>)}
            </ul>
          </div>

          {/* CTA */}
          <div className="flex items-center text-cyan-400 font-semibold text-sm group-hover:text-cyan-300 transition-colors" data-id="element-1628">
            View Guide
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" data-id="element-1629" />
          </div>
        </div>

        {/* Scanline Effect */}
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(6,182,212,0.02)_50%)] bg-[length:100%_2px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1630"></div>
      </Card>
    </Link>;
}